import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { classNames } from '../utils/classNames';

interface Provider {
  id: number;
  name: string;
  api_key: string;
  webhook_url: string | null;
  ip_allowlist: string | null;
  is_active: boolean;
  amount_divisor?: number;
  created_at: string;
  updated_at: string | null;
  shops?: Shop[];
}

interface Shop {
  id: number;
  name: string;
}

interface CreateProviderData {
  name: string;
  apiKey: string;
  webhookUrl: string;
  ipAllowlist: string;
  isActive: boolean;
  shopIds: number[];
}

interface ProviderMessage {
  id: string;
  provider_id: number;
  provider_name: string;
  amount: number;
  currency: string;
  sender_name: string | null;
  sender_account: string | null;
  ts: string;
  meta: string | null;
  device_id: number | null;
  received_at: string | null;
  operation_type: string | null;
}

const ProviderManagement: React.FC = () => {
  const { user } = useAuth();

  // Функция для извлечения device name из метаданных сообщения провайдера
  const getDeviceNameFromMeta = (meta: string | null): string => {
    if (!meta) return 'Не указано';
    try {
      const metaData = JSON.parse(meta);
      return metaData?.deviceInfo?.name || 'Не указано';
    } catch {
      return 'Не указано';
    }
  };
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [activeTab, setActiveTab] = useState<'providers' | 'messages'>('providers');
  const [providerMessages, setProviderMessages] = useState<ProviderMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [editingForm, setEditingForm] = useState<{
    name: string;
    webhookUrl: string;
    ipAllowlist: string;
    isActive: boolean;
    shopIds: number[];
    amountDivisor: number;
  }>({
    name: '',
    webhookUrl: '',
    ipAllowlist: '',
    isActive: true,
    shopIds: [],
    amountDivisor: 1
  });
  const [createForm, setCreateForm] = useState<CreateProviderData>({
    name: '',
    apiKey: '',
    webhookUrl: '',
    ipAllowlist: '',
    isActive: true,
    shopIds: []
  });
  const [createAmountDivisor, setCreateAmountDivisor] = useState<number>(1);
  const [shops, setShops] = useState<Shop[]>([]);

  useEffect(() => {
    if (user?.role === 'owner') {
      fetchProviders();
      fetchShops();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'messages' && user?.role === 'owner') {
      fetchProviderMessages();
    }
  }, [activeTab, user?.role]);

  const fetchProviders = async () => {
    try {
      const response = await fetch('/api/owner/providers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await fetch('/api/owner/shops', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setShops(data);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchProviderMessages = async () => {
    if (user?.role !== 'owner') return;
    
    setLoadingMessages(true);
    try {
      const response = await fetch('/api/owner/provider-messages', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProviderMessages(data);
      }
    } catch (error) {
      console.error('Error fetching provider messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCreateProvider = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/owner/providers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: createForm.name,
          apiKey: createForm.apiKey,
          webhookUrl: createForm.webhookUrl || null,
          ipAllowlist: createForm.ipAllowlist || null,
          isActive: createForm.isActive,
          shopIds: createForm.shopIds,
          amountDivisor: Math.max(1, Number.isFinite(createAmountDivisor as any) ? Number(createAmountDivisor) : 1)
        })
      });

      if (response.ok) {
        setShowCreateForm(false);
        setCreateForm({
          name: '',
          apiKey: '',
          webhookUrl: '',
          ipAllowlist: '',
          isActive: true,
          shopIds: []
        });
        setCreateAmountDivisor(1);
        fetchProviders();
      }
    } catch (error) {
      console.error('Error creating provider:', error);
    }
  };

  const handleEditProvider = (provider: Provider) => {
    setEditingProvider(provider);
    setEditingForm({
      name: provider.name,
      webhookUrl: provider.webhook_url || '',
      ipAllowlist: provider.ip_allowlist || '',
      isActive: provider.is_active,
      shopIds: provider.shops?.map(shop => shop.id) || [],
      amountDivisor: provider.amount_divisor || 1
    });
  };

  const handleUpdateProvider = async (provider: Provider) => {
    try {
      const response = await fetch(`/api/owner/providers/${provider.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: editingForm.name,
          webhookUrl: editingForm.webhookUrl || null,
          ipAllowlist: editingForm.ipAllowlist || null,
          isActive: editingForm.isActive,
          shopIds: editingForm.shopIds,
          amountDivisor: Math.max(1, Number.isFinite(editingForm.amountDivisor as any) ? Number(editingForm.amountDivisor) : 1)
        })
      });

      if (response.ok) {
        setEditingProvider(null);
        setEditingForm({
          name: '',
          webhookUrl: '',
          ipAllowlist: '',
          isActive: true,
          shopIds: [],
          amountDivisor: 1
        });
        fetchProviders();
      }
    } catch (error) {
      console.error('Error updating provider:', error);
    }
  };

  const handleDeleteProvider = async (providerId: number, forceDelete = false) => {
    const confirmMessage = forceDelete 
      ? 'Вы уверены, что хотите ПРИНУДИТЕЛЬНО удалить этого провайдера со всеми его транзакциями? Это действие необратимо!'
      : 'Вы уверены, что хотите удалить этого провайдера?';
      
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      const url = forceDelete 
        ? `/api/owner/providers/${providerId}?force=true`
        : `/api/owner/providers/${providerId}`;
        
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchProviders();
        const result = await response.json();
        alert(result.message || 'Провайдер успешно удален');
      } else {
        const errorData = await response.json();
        
        // Если есть возможность принудительного удаления, предлагаем её
        if (errorData.canForceDelete && !forceDelete) {
          const forceConfirm = confirm(
            `${errorData.error}\n\nХотите принудительно удалить провайдера со всеми транзакциями (${errorData.transactionsCount} шт.)?`
          );
          
          if (forceConfirm) {
            handleDeleteProvider(providerId, true);
            return;
          }
        }
        
        alert(`Ошибка удаления провайдера: ${errorData.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert('Произошла ошибка при удалении провайдера');
    }
  };

  const generateApiKey = () => {
    const key = `provider_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    setCreateForm(prev => ({ ...prev, apiKey: key }));
  };

  if (user?.role !== 'owner') {
    return <div className="flex min-h-[40vh] items-center justify-center text-danger">Доступ запрещен</div>;
  }

  if (loading) {
    return <div className="flex min-h-[40vh] items-center justify-center text-[var(--app-text-muted)]">Загрузка…</div>;
  }

  return (
    <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold text-[var(--app-heading)]">Управление провайдерами</h1>
            <p className="text-sm text-[var(--app-text-muted)]">Управляйте настройками провайдеров и отслеживайте их сообщения.</p>
          </div>
          {activeTab === 'providers' && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
            >
              Добавить провайдера
            </button>
          )}
        </div>

        {/* Вкладки */}
        <div className="border-b border-[var(--app-border)] pb-2">
          <nav className="-mb-px flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('providers')}
              className={classNames(
                'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]',
                activeTab === 'providers'
                  ? 'border-primary-200 bg-primary-50 text-primary-600 shadow-sm'
                  : 'border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text-muted)] hover:border-primary-200 hover:text-primary-600'
              )}
            >
              <span className="mr-2">🔌</span>
              Провайдеры
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={classNames(
                'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]',
                activeTab === 'messages'
                  ? 'border-primary-200 bg-primary-50 text-primary-600 shadow-sm'
                  : 'border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text-muted)] hover:border-primary-200 hover:text-primary-600'
              )}
            >
              <span className="mr-2">📡</span>
              Сообщения провайдеров
            </button>
          </nav>
        </div>

        {/* Содержимое вкладки "Провайдеры" */}
        {activeTab === 'providers' && (
          <div className="space-y-4">
            <ul className="divide-y divide-[var(--app-border)]">
            {providers.map((provider) => (
              <li key={provider.id} className="card">
                <div className="card-body space-y-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1 space-y-3">
                      <div>
                        <h3 className="text-lg font-medium text-[var(--app-heading)]">{provider.name}</h3>
                        <p className="text-sm text-[var(--app-text-muted)]">
                          <span className="font-semibold text-[var(--app-text)]">API ключ провайдера:</span>{' '}
                          <code className="inline-flex break-all rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-2 py-1 text-xs text-[var(--app-text)]">
                            {provider.api_key}
                          </code>
                        </p>
                        <p className="mt-1 text-sm text-[var(--app-text-muted)]">
                          <span className="font-medium text-[var(--app-text)]">Делитель суммы:</span> x{provider.amount_divisor || 1}
                        </p>
                        <p className="text-xs text-[var(--app-text-muted)]">
                          ⚠️ Это ключ для провайдера, НЕ для магазина! Магазины имеют свои отдельные API ключи.
                        </p>
                        <div className="mt-3 space-y-2 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] p-4">
                          <p className="text-sm font-semibold text-[var(--app-heading)]">Ссылка для отправки данных:</p>
                          <code className="block break-all rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] px-2 py-1 text-xs text-[var(--app-text)]">
                            {'http://localhost:5000/api/provider/v1/transactions/push'}
                          </code>
                          <p className="text-xs text-[var(--app-text-muted)]">
                            Используйте API Key: <strong>{provider.api_key}</strong>
                          </p>

                          <details className="mt-2 text-xs text-[var(--app-text-muted)]">
                            <summary className="cursor-pointer text-xs font-semibold text-primary-500 hover:text-primary-400">
                              Показать примеры JSON
                            </summary>
                            <div className="mt-2 space-y-2">
                              <div className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-xs">
                                <p className="font-medium text-[var(--app-heading)]">Полный формат:</p>
                                <pre className="overflow-x-auto text-[var(--app-text)]">
{`{
  "apiKey": "${provider.api_key}",
  "transactionId": "test-1756548992744",
  "timestamp": "2025-08-30T10:16:32.744Z",
  "receivedAt": "2025-08-30T10:16:32.744Z",
  "deviceId": 8,
  "parsedData": {
    "bank": "Test Bank",
    "amount": 1000,
    "currency": "KZT",
    "operationType": "debit",
    "senderName": "Test Sender Name",
    "balance": 50000,
    "balanceCurrency": "KZT",
    "transactionTime": "2025-08-30T10:16:32.744Z",
    "merchantName": "Test Merchant"
  },
  "deviceInfo": {
    "name": "Daniiar",
    "description": "Test Device Description"
  }
}`}
                                </pre>
                              </div>

                              <div className="rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-xs">
                                <p className="font-medium text-[var(--app-heading)]">Упрощенный формат:</p>
                                <pre className="overflow-x-auto text-[var(--app-text)]">
{`{
  "apiKey": "${provider.api_key}",
  "parsedData": {
    "amount": 3000,
    "currency": "KZT",
    "operationType": "income",
    "senderName": "Гульмира С."
  },
  "deviceInfo": {
    "name": "Altynbek",
    "description": null
  },
  "timestamp": "2025-08-30 15:19:35"
}`}
                                </pre>
                                <p className="text-xs text-[var(--app-text-muted)]">
                                  Недостающие поля заполняются автоматически
                                </p>
                              </div>
                            </div>
                          </details>
                        </div>

                        {provider.webhook_url && (
                          <p className="text-sm text-[var(--app-text-muted)] mt-2">
                            <span className="font-medium">Webhook:</span> {provider.webhook_url}
                          </p>
                        )}
                        {provider.ip_allowlist && (
                          <p className="text-sm text-[var(--app-text-muted)]">
                            <span className="font-medium">IP Allowlist:</span> {provider.ip_allowlist}
                          </p>
                        )}
                        {provider.shops && provider.shops.length > 0 && (
                          <p className="text-sm text-[var(--app-text-muted)]">
                            <span className="font-medium">Магазины:</span> {provider.shops.map(shop => shop.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          provider.is_active 
                            ? 'bg-success/15 text-success' 
                            : 'bg-danger/15 text-danger'
                        }`}>
                          {provider.is_active ? 'Активен' : 'Неактивен'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleEditProvider(provider)}
                      className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-600 transition hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                    >
                      Редактировать
                    </button>
                    <button
                      onClick={() => handleDeleteProvider(provider.id)}
                      className="inline-flex items-center rounded-full bg-danger/10 px-3 py-1 text-xs font-semibold text-danger transition hover:bg-danger/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}

        {/* Форма создания провайдера */}
        {showCreateForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--app-bg)]/80 backdrop-blur-sm">
            <div className="w-full max-w-xl space-y-4 rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-lifted">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-[var(--app-heading)]">Создать провайдера</h3>
                <p className="text-sm text-[var(--app-text-muted)]">Укажите реквизиты провайдера и доступные магазины.</p>
              </div>
              <form onSubmit={handleCreateProvider} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Название</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">API ключ провайдера</label>
                    <p className="text-xs text-[var(--app-text-muted)] mb-1">Уникальный ключ для этого провайдера платежей</p>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={createForm.apiKey}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, apiKey: e.target.value }))}
                        className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                        required
                      />
                      <button
                        type="button"
                        onClick={generateApiKey}
                        className="mt-1 inline-flex items-center rounded-full bg-primary-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                      >
                        Генерировать
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Делитель суммы</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={createAmountDivisor}
                      onChange={(e) => {
                        const value = e.target.value || '1';
                        const parsed = parseInt(value, 10);
                        setCreateAmountDivisor(Math.max(1, isNaN(parsed) ? 1 : parsed));
                      }}
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                      required
                    />
                    <p className="text-xs text-[var(--app-text-muted)] mt-1">Применяется к суммам из сообщений провайдера до мэчинга</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Webhook URL</label>
                    <input
                      type="url"
                      value={createForm.webhookUrl}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">IP Allowlist</label>
                    <input
                      type="text"
                      value={createForm.ipAllowlist}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, ipAllowlist: e.target.value }))}
                      placeholder="127.0.0.1,192.168.1.0/24"
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Магазины</label>
                    <div className="mt-1 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {shops.map(shop => (
                        <label key={shop.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            checked={createForm.shopIds.includes(shop.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCreateForm(prev => ({ 
                                  ...prev, 
                                  shopIds: [...prev.shopIds, shop.id] 
                                }));
                              } else {
                                setCreateForm(prev => ({ 
                                  ...prev, 
                                  shopIds: prev.shopIds.filter(id => id !== shop.id) 
                                }));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-900">{shop.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Удерживайте Ctrl (Cmd на Mac) для выбора нескольких магазинов. 
                      Провайдер будет работать только с выбранными магазинами.
                    </p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={createForm.isActive}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 rounded border-[var(--app-border)] text-primary-500 focus:ring-primary-400"
                    />
                    <label className="ml-2 text-sm text-[var(--app-text)]">Активен</label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="inline-flex items-center rounded-full bg-[var(--app-surface-subtle)] px-4 py-2 text-sm font-semibold text-[var(--app-text-muted)] transition hover:text-[var(--app-text)]"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                    >
                      Создать
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Форма редактирования провайдера */}
        {editingProvider && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--app-bg)]/80 backdrop-blur-sm">
            <div className="w-full max-w-xl space-y-4 rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-lifted">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-[var(--app-heading)]">Редактировать провайдера</h3>
                <p className="text-sm text-[var(--app-text-muted)]">Обновите параметры подключения и доступные магазины.</p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateProvider(editingProvider); }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Название</label>
                    <input
                      type="text"
                      value={editingForm.name}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Webhook URL</label>
                    <input
                      type="url"
                      value={editingForm.webhookUrl}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Делитель суммы</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={editingForm.amountDivisor}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, amountDivisor: Math.max(1, parseInt(e.target.value || '1') || 1) }))}
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                      required
                    />
                    <p className="text-xs text-[var(--app-text-muted)] mt-1">Применяется к суммам из сообщений провайдера до мэчинга</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">IP Allowlist</label>
                    <input
                      type="text"
                      value={editingForm.ipAllowlist}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, ipAllowlist: e.target.value }))}
                      placeholder="127.0.0.1,192.168.1.0/24"
                      className="mt-1 block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-3 py-2 text-sm text-[var(--app-text)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--app-text)]">Магазины</label>
                    <div className="mt-1 max-h-32 overflow-y-auto rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] p-3">
                      {shops.map(shop => (
                        <label key={shop.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            checked={editingForm.shopIds.includes(shop.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditingForm(prev => ({ 
                                  ...prev, 
                                  shopIds: [...prev.shopIds, shop.id] 
                                }));
                              } else {
                                setEditingForm(prev => ({ 
                                  ...prev, 
                                  shopIds: prev.shopIds.filter(id => id !== shop.id) 
                                }));
                              }
                            }}
                            className="h-4 w-4 rounded border-[var(--app-border)] text-primary-500 focus:ring-primary-400"
                          />
                          <span className="text-sm text-[var(--app-text)]">{shop.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingForm.isActive}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 rounded border-[var(--app-border)] text-primary-500 focus:ring-primary-400"
                    />
                    <label className="ml-2 text-sm text-[var(--app-text)]">Активен</label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingProvider(null)}
                      className="inline-flex items-center rounded-full bg-[var(--app-surface-subtle)] px-4 py-2 text-sm font-semibold text-[var(--app-text-muted)] transition hover:text-[var(--app-text)]"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]"
                    >
                      Сохранить
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Содержимое вкладки "Сообщения провайдеров" */}
        {activeTab === 'messages' && (
          <div className="card">
            <div className="card-body space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-[var(--app-heading)]">Последние сообщения от провайдеров</h3>
              <button
                onClick={fetchProviderMessages}
                disabled={loadingMessages}
                className="inline-flex items-center rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)] disabled:cursor-not-allowed disabled:bg-primary-500/60"
              >
                {loadingMessages ? (
                  <>
                    <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Загрузка...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    Обновить
                  </>
                )}
              </button>
            </div>
 
             <div className="space-y-4">
               {providerMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-subtle)] px-6 py-10 text-center">
                  <div className="mb-3 text-5xl text-[var(--app-text-muted)]">📡</div>
                  <h4 className="text-lg font-semibold text-[var(--app-heading)]">Нет сообщений от провайдеров</h4>
                  <p className="mt-1 text-sm text-[var(--app-text-muted)]">
                    Здесь будут отображаться последние 15 сообщений от всех провайдеров платежей
                  </p>
                </div>
               ) : (
                 <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[var(--app-border)]">
                    <thead className="bg-[var(--app-surface-subtle)] text-xs font-semibold uppercase tracking-wide text-[var(--app-text-muted)]">
                      <tr>
                        <th className="px-5 py-3 text-left">Время</th>
                        <th className="px-5 py-3 text-left">Провайдер</th>
                        <th className="px-5 py-3 text-left">ID транзакции</th>
                        <th className="px-5 py-3 text-left">Сумма</th>
                        <th className="px-5 py-3 text-left">Отправитель</th>
                        <th className="px-5 py-3 text-left">Счет/Карта</th>
                        <th className="px-5 py-3 text-left">Тип операции</th>
                        <th className="px-5 py-3 text-left">Статус</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--app-border)] bg-[var(--app-surface)] text-sm text-[var(--app-text)]">
                      {providerMessages.map((message, index) => (
                        <tr key={message.id || index} className="hover:bg-[var(--app-hover)]/40">
                          <td className="whitespace-nowrap px-5 py-4 text-sm text-[var(--app-text)]">
                            {message.received_at ? 
                              new Date(message.received_at).toLocaleString('ru-RU') : 
                              new Date(message.ts).toLocaleString('ru-RU')
                            }
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-600">
                              {message.provider_name || `ID: ${message.provider_id}`}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 font-mono text-sm">
                            {message.id}
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            {message.amount} {message.currency}
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            {message.sender_name || 'Не указано'}
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 font-mono text-sm">
                            {getDeviceNameFromMeta(message.meta)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            <span className={classNames(
                              'inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold',
                              message.operation_type === 'incoming'
                                ? 'bg-success/15 text-success'
                                : message.operation_type === 'outgoing'
                                ? 'bg-danger/15 text-danger'
                                : 'bg-[var(--app-surface-subtle)] text-[var(--app-text-muted)]'
                            )}>
                              {message.operation_type === 'incoming' ? 'Входящий' : 
                               message.operation_type === 'outgoing' ? 'Исходящий' : 
                               message.operation_type || 'Неизвестно'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-5 py-4 text-sm">
                            <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-600">
                              Получено
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
 
             {providerMessages.length > 0 && (
              <div className="border-t border-[var(--app-border)] pt-4 text-center text-sm text-[var(--app-text-muted)]">
                Показано последние {providerMessages.length} сообщений от всех провайдеров
              </div>
            )}
            </div>
          </div>
        )}
      </div>
  );
};

export default ProviderManagement;
